/**
 * The survey, in the Aurora Atlas language.
 *
 * Two rules shape this component:
 *
 *  - The free-text box is on the same screen as the questions, never behind a
 *    "tell us more" link. The text is the payload; the scored questions are how
 *    we sort it.
 *  - Nothing is required. A half-filled survey is data; a form that refuses to
 *    submit is silence. The only thing we insist on is that *something* was
 *    said, which the server enforces too.
 */

import { useState } from 'react';
import { Loader2, Check, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { glassPanel, glassHero, inkMuted } from './learnShared';
import { submitFeedback } from '../../services/feedbackAPI';
import { getReviewerKey } from './learnShared';
import type { FormSet, Question } from './feedbackForms';

const FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B2EE5]';

function ScaleRow({ q, value, onChange }: { q: Question; value?: number; onChange: (v: number) => void }) {
  return (
    <div>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            aria-pressed={value === n}
            className={cn(
              'h-11 flex-1 rounded-xl text-[15px] font-bold tabular-nums transition-all',
              FOCUS,
              value === n
                ? 'bg-[#5B2EE5] text-white shadow-[0_8px_20px_-10px_rgba(91,46,229,0.9)]'
                : 'border border-white/70 bg-white/70 hover:bg-white dark:border-white/10 dark:bg-white/[0.07] dark:hover:bg-white/[0.12]',
            )}
          >
            {n}
          </button>
        ))}
      </div>
      {(q.lowLabel || q.highLabel) && (
        <div className={cn('mt-1.5 flex justify-between text-[11.5px]', inkMuted)}>
          <span>{q.lowLabel}</span>
          <span>{q.highLabel}</span>
        </div>
      )}
    </div>
  );
}

function ChoiceRow({
  q,
  value,
  onChange,
  multi,
}: {
  q: Question;
  value?: string | string[];
  onChange: (v: string | string[]) => void;
  multi?: boolean;
}) {
  const selected = multi ? (Array.isArray(value) ? value : []) : value;
  const toggle = (v: string) => {
    if (!multi) return onChange(v);
    const cur = Array.isArray(value) ? value : [];
    onChange(cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {q.choices?.map((c) => {
        const on = multi ? (selected as string[]).includes(c.value) : selected === c.value;
        return (
          <button
            key={c.value}
            type="button"
            onClick={() => toggle(c.value)}
            aria-pressed={on}
            className={cn(
              'rounded-xl px-3.5 py-2.5 text-[14px] font-semibold transition-all',
              FOCUS,
              on
                ? 'bg-[#5B2EE5] text-white shadow-[0_8px_20px_-10px_rgba(91,46,229,0.9)]'
                : 'border border-white/70 bg-white/70 hover:bg-white dark:border-white/10 dark:bg-white/[0.07] dark:hover:bg-white/[0.12]',
            )}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

const textareaCls =
  'w-full rounded-xl border border-white/70 bg-white/85 px-3.5 py-3 text-[14.5px] leading-relaxed outline-none placeholder:text-[#8A8593] focus:border-[#5B2EE5] dark:border-white/12 dark:bg-white/[0.07]';

export function FeedbackSurvey({
  form,
  role,
  shareToken,
  courseId,
  chapter,
  source,
  learnerAccountId,
  prefillName,
  prefillEmail,
  askIdentity,
  onDone,
  className,
}: {
  form: FormSet;
  /** Overrides the form's default role — used where the role is asked up front. */
  role?: string;
  shareToken?: string | null;
  courseId?: string | null;
  chapter?: string | null;
  source?: string | null;
  learnerAccountId?: string | null;
  prefillName?: string | null;
  prefillEmail?: string | null;
  /** Show the optional name / school / email block. Never required. */
  askIdentity?: boolean;
  onDone?: () => void;
  className?: string;
}) {
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [comment, setComment] = useState('');
  const [cannotExplain, setCannotExplain] = useState('');
  const [wishlist, setWishlist] = useState('');
  const [priority, setPriority] = useState<number | undefined>();
  const [name, setName] = useState(prefillName ?? '');
  const [email, setEmail] = useState(prefillEmail ?? '');
  const [organisation, setOrganisation] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (id: string, v: unknown) => setAnswers((a) => ({ ...a, [id]: v }));

  const hasSomething =
    Object.keys(answers).length > 0 || comment.trim() || cannotExplain.trim() || wishlist.trim();

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await submitFeedback({
        subject: form.subject,
        role: (role ?? form.role) as string,
        formKey: form.key,
        shareToken: shareToken ?? null,
        courseId: courseId ?? null,
        chapter: chapter ?? null,
        answers,
        comment: comment.trim() || null,
        cannotExplain: cannotExplain.trim() || null,
        wishlist: wishlist.trim() || null,
        priority: priority ?? null,
        learnerAccountId: learnerAccountId ?? null,
        name: name.trim() || null,
        email: email.trim() || null,
        organisation: organisation.trim() || null,
        respondentKey: getReviewerKey(),
        source: source ?? null,
        website: honeypot,
      });
      setDone(true);
      onDone?.();
    } catch {
      setError('That did not send. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className={cn(glassPanel, 'p-6 text-center', className)}>
        <span className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-full bg-[#17B08B]/15">
          <Check className="h-5 w-5 text-[#0E8A6B] dark:text-[#3DDBB0]" />
        </span>
        <p className="font-serif text-[22px] leading-[1.28] tracking-tight">Thank you — that helps.</p>
        <p className={cn('mt-1.5 text-[14px]', inkMuted)}>
          {form.demand
            ? 'We read every one of these. We cannot promise we will build all of it.'
            : 'Your answers go straight to the people making the next lesson.'}
        </p>
      </div>
    );
  }

  return (
    <section className={cn(glassHero, 'p-6 sm:p-7', className)}>
      <h2 className="font-serif text-[26px] leading-[1.28] tracking-tight">{form.title}</h2>
      <p className={cn('mt-1.5 text-[14.5px] leading-relaxed', inkMuted)}>{form.intro}</p>

      <div className="mt-6 flex flex-col gap-6">
        {form.questions.map((q, i) => (
          <div key={q.id}>
            <label className="block text-[15px] font-semibold leading-[1.45]">
              <span className={cn('mr-1.5 tabular-nums', inkMuted)}>{i + 1}.</span>
              {q.prompt}
            </label>
            <div className="mt-2.5">
              {q.type === 'scale' && (
                <ScaleRow q={q} value={answers[q.id] as number | undefined} onChange={(v) => set(q.id, v)} />
              )}
              {(q.type === 'choice' || q.type === 'multi') && (
                <ChoiceRow
                  q={q}
                  multi={q.type === 'multi'}
                  value={answers[q.id] as string | string[] | undefined}
                  onChange={(v) => set(q.id, v)}
                />
              )}
              {q.type === 'text' && (
                <textarea
                  rows={2}
                  value={(answers[q.id] as string) ?? ''}
                  onChange={(e) => set(q.id, e.target.value)}
                  className={textareaCls}
                  placeholder="In your own words…"
                />
              )}
            </div>
          </div>
        ))}

        {/* Demand capture — the reason this form exists. */}
        {form.demand && (
          <>
            <div>
              <label className="block text-[15px] font-semibold leading-[1.45]">
                What do you find hardest to explain with the materials you have today?
              </label>
              <p className={cn('mt-1 text-[13px]', inkMuted)}>
                The honest answer here is worth more to us than everything above.
              </p>
              <textarea
                rows={3}
                value={cannotExplain}
                onChange={(e) => setCannotExplain(e.target.value)}
                className={cn(textareaCls, 'mt-2')}
                placeholder="The thing students never quite get, however you teach it…"
              />
            </div>
            <div>
              <label className="block text-[15px] font-semibold leading-[1.45]">
                What should we build next for this subject and grade?
              </label>
              <textarea
                rows={2}
                value={wishlist}
                onChange={(e) => setWishlist(e.target.value)}
                className={cn(textareaCls, 'mt-2')}
                placeholder="A topic, a chapter, a kind of practice — anything, built or not."
              />
              <div className="mt-3">
                <p className={cn('mb-1.5 text-[13.5px] font-semibold', inkMuted)}>
                  If we built that, how much would it help?
                </p>
                <ScaleRow
                  q={{ id: 'priority', prompt: '', type: 'scale', lowLabel: 'A little', highLabel: 'Enormously' }}
                  value={priority}
                  onChange={setPriority}
                />
              </div>
            </div>
          </>
        )}

        {/* Always visible: the text is the payload. */}
        <div>
          <label className="block text-[15px] font-semibold leading-[1.45]">{form.commentPrompt}</label>
          <textarea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className={cn(textareaCls, 'mt-2')}
            placeholder="Optional, but read by a human."
          />
        </div>

        {askIdentity && (
          <div>
            <p className={cn('text-[13.5px] font-semibold', inkMuted)}>
              Who are you? <span className="font-normal">Optional — only so we can come back to you.</span>
            </p>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name"
                className="rounded-xl border border-white/70 bg-white/85 px-3.5 py-2.5 text-[14px] outline-none focus:border-[#5B2EE5] dark:border-white/12 dark:bg-white/[0.07]" />
              <input value={organisation} onChange={(e) => setOrganisation(e.target.value)} placeholder="School"
                className="rounded-xl border border-white/70 bg-white/85 px-3.5 py-2.5 text-[14px] outline-none focus:border-[#5B2EE5] dark:border-white/12 dark:bg-white/[0.07]" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email"
                className="rounded-xl border border-white/70 bg-white/85 px-3.5 py-2.5 text-[14px] outline-none focus:border-[#5B2EE5] dark:border-white/12 dark:bg-white/[0.07]" />
            </div>
          </div>
        )}

        {/* Honeypot — off-screen, never announced, never focusable. */}
        <input
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="pointer-events-none absolute left-[-9999px] h-0 w-0 opacity-0"
          placeholder="Leave blank"
        />
      </div>

      {error && <p className="mt-4 text-[13.5px] text-[#B4341F] dark:text-[#FF8A73]">{error}</p>}

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={busy || !hasSomething}
          className={cn(
            'inline-flex items-center gap-2 rounded-xl bg-[#5B2EE5] px-6 py-3 text-[15px] font-bold text-white shadow-[0_10px_28px_-12px_rgba(91,46,229,0.9)] transition-transform hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100',
            FOCUS,
          )}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
          Send
        </button>
        {form.demand && (
          <span className={cn('text-[12.5px]', inkMuted)}>
            We read every one. No promises on what gets built.
          </span>
        )}
      </div>
    </section>
  );
}
